import { BellRing, Smartphone } from "lucide-react";
import type { PushStatus } from "@metrickle/schema";
import { fmtAgo } from "../lib/format";
import { isAppleMobile, isInstalled, thisDeviceId, useRemovePushDevice, useTestPush, useThisDevice, useTurnOffPushHere, useTurnOnPush } from "../lib/push";
import { track } from "../lib/track";
import { Button, SectionLabel, notifyError, toaster } from "./ui";

const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

/**
 * Push on this device, and every device push is on for. Which events are pushed is the Push column
 * in the list below it. Shown only when the server has push set up.
 */
export function PushSettings({ status }: { status: PushStatus }) {
  const here = useThisDevice();
  const turnOn = useTurnOnPush();
  const turnOff = useTurnOffPushHere();
  const remove = useRemovePushDevice();
  const test = useTestPush();
  const busy = turnOn.isPending || turnOff.isPending;
  const state = here.state;
  // Subscribed in this browser, and the server still has it.
  const mine = thisDeviceId();
  const on = !!state?.endpoint && status.devices.some((d) => d.id === mine);

  const enable = () =>
    turnOn.mutate(status.publicKey!, {
      onSuccess: () => {
        track("push_turned_on", { installed: isInstalled() });
        toaster.success({ title: "Push notifications are on for this device" });
        void here.refresh();
      },
      onError: (err) => {
        notifyError(err);
        void here.refresh();
      },
    });

  const disable = (from: "this_device" | "device_list") =>
    turnOff.mutate(undefined, {
      onSuccess: () => {
        track("push_turned_off", { from });
        toaster.success({ title: "Push notifications are off for this device" });
        void here.refresh();
      },
      onError: notifyError,
    });

  return (
    <section aria-labelledby="push-heading" className="space-y-3 rounded-lg border border-line p-3">
      <div className="flex items-start gap-3">
        <BellRing size={18} aria-hidden className="mt-0.5 shrink-0 text-ink-3" />
        <div className="min-w-0 flex-1">
          <h3 id="push-heading" className="text-sm font-medium text-ink">
            Push notifications on this device
          </h3>
          <p className="text-xs text-ink-3" aria-live="polite">
            {!state
              ? "Checking this device…"
              : state.permission === "unsupported"
                ? isAppleMobile() && !isInstalled()
                  ? "On iPhone and iPad, push works in the Metrickle app. In Safari, tap Share, then Add to Home Screen. Open Metrickle from your Home Screen and turn push on there."
                  : "This browser can't receive push notifications. Try a current version of Chrome, Edge, Firefox or Safari."
                : state.permission === "denied"
                  ? "Notifications are blocked for this site. Allow them in your browser or phone settings, then turn push on here."
                  : on
                    ? "On. Alerts you've chosen to push below reach this device, even when Metrickle isn't open."
                    : "Get alerts about lost conversions on this device, even when Metrickle isn't open."}
          </p>
        </div>
      </div>
      {state && state.permission !== "unsupported" && (
        <div className="flex flex-wrap gap-2">
          {on ? (
            <>
              <Button
                size="sm"
                disabled={test.isPending}
                onClick={() =>
                  test.mutate(undefined, {
                    onSuccess: (r) => {
                      track("push_test_sent", { devices: r.sent });
                      toaster.success({
                        title: r.sent ? `Test sent to ${r.sent} device${r.sent === 1 ? "" : "s"}` : "The test didn't reach any device",
                        description: r.sent ? "It should arrive in a few seconds." : "Try turning push off and on again on this device.",
                      });
                    },
                    onError: notifyError,
                  })
                }
              >
                {test.isPending ? "Sending…" : "Send a test"}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => disable("this_device")}>
                Turn off on this device
              </Button>
            </>
          ) : (
            <Button size="sm" variant="primary" disabled={busy || state.permission === "denied"} onClick={enable}>
              {turnOn.isPending ? "Turning on…" : "Turn on push for this device"}
            </Button>
          )}
        </div>
      )}
      {status.devices.length > 0 && (
        <div>
          <SectionLabel as="h4" className="mb-1.5">Your devices</SectionLabel>
          <ul className="divide-y divide-line rounded-lg border border-line">
            {status.devices.map((d) => {
              const current = d.id === mine && on;
              return (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2">
                  <div className="flex min-w-0 items-start gap-2">
                    <Smartphone size={15} aria-hidden className="mt-0.5 shrink-0 text-ink-3" />
                    <div className="min-w-0">
                      <p className="text-sm text-ink">
                        {d.label}
                        {current && <span className="ml-1.5 text-xs text-ink-3">(this device)</span>}
                      </p>
                      <p className="text-xs text-ink-3">
                        Added {fmtDay(d.createdAt)} · {d.lastPushAt ? `last notification ${fmtAgo(d.lastPushAt)}` : "no notifications yet"}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${d.label}${current ? " (this device)" : ""}`}
                    disabled={remove.isPending || busy}
                    onClick={() =>
                      current
                        ? disable("device_list")
                        : remove.mutate(d.id, {
                            onSuccess: () => {
                              track("push_turned_off", { from: "device_list" });
                              toaster.success({ title: `Removed ${d.label}` });
                            },
                            onError: notifyError,
                          })
                    }
                  >
                    Remove
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
